/* Native Shopify pricing remains authoritative. This client never overrides a cart price. */
(function (root) {
  'use strict';
  const EXPERIMENT = 'flipzy-price-abc-v1';
  const STORAGE = 'flipzy-price-abc-v1';
  const ATTRS = { experimentId: '_flipzy_experiment', arm: '_flipzy_arm', visitorId: '_flipzy_visitor', token: '_flipzy_assignment' };
  const EXCLUDED_ATTRIBUTE = '_flipzy_price_excluded';
  const EXCLUDED_VALUE = 'native-unavailable-v1';
  const OWNED_ATTRIBUTES = [...Object.values(ATTRS), '_flipzy_price_cohort', '_flipzy_currency', '_flipzy_base_cents', '_flipzy_pack_cents'];

  function validAssignment(value, currency, qa = false) {
    return value?.eligible === true && (value.qa === true) === qa && value.experimentId === EXPERIMENT && ['A', 'B', 'C'].includes(value.arm)
      && typeof value.token === 'string' && value.token.length > 20 && typeof value.visitorId === 'string'
      && value.currency === currency && Number.isSafeInteger(value.prices?.baseCents) && value.prices.baseCents > 0
      && Number.isSafeInteger(value.prices?.packCents) && value.prices.packCents > 0 && value.includedPacksPerBase === 1;
  }

  function expectedTotal(items, catalog, prices, currency = 'EUR') {
    let bases = 0, packs = 0, actual = 0;
    const lines = [];
    for (const item of items) {
      const product = catalog.find((entry) => String(entry.productId) === String(item.product_id));
      if (!product) continue;
      if (String(product.variantId) !== String(item.variant_id || item.id) || !Number.isSafeInteger(item.quantity)
        || item.quantity < 1 || !Number.isSafeInteger(item.final_line_price)) throw new Error('Invalid experiment cart');
      if (product.role === 'base') bases += item.quantity;
      else packs += item.quantity;
      actual += item.final_line_price;
      lines.push({ item, role: product.role });
    }
    const percent = bases >= 4 ? 20 : bases >= 3 ? 10 : 0;
    const quantum = currency === 'ISK' ? 100 : 1;
    const baseUnit = prices.baseCents - Math.floor(prices.baseCents * percent / (100 * quantum)) * quantum;
    const expected = bases * baseUnit
      + Math.max(0, packs - bases) * prices.packCents;
    let included = 0;
    const validLines = lines.every(({ item, role }) => {
      const unit = role === 'base' ? prices.baseCents : prices.packCents;
      if (item.original_line_price != null && item.original_line_price !== item.quantity * unit) return false;
      if (role === 'base') return item.final_line_price === item.quantity * baseUnit;
      if (item.final_line_price < 0 || item.final_line_price > item.quantity * unit || item.final_line_price % unit !== 0) return false;
      included += item.quantity - item.final_line_price / unit;
      return true;
    });
    return { expected, actual, units: bases + packs, valid: validLines && included === Math.min(packs, bases) };
  }

  function createClient(config, env) {
    const state = { active: false, blocked: false, recoverable: false, failureReason: null, retryAt: 0,
      recoverableDiscount: false, discountCodes: [], assignment: null, baseline: null, ready: null };
    const discountCodes = (cart) => [...new Set((cart.discount_codes || []).map((entry) => String(typeof entry === 'string' ? entry : entry?.code || '').trim()).filter(Boolean))];
    const warn = (reason = 'price_mismatch', extra = {}) => Object.assign(new Error(reason === 'unavailable'
      ? config.unavailableWarning || 'Prices could not be checked. Please try again.'
      : config.warning || 'Please review your cart before checkout.'), { reason, ...extra });
    const now = () => env.now?.() ?? Date.now();
    const unavailableBlockers = new Set(['shopify_readiness_unavailable', 'shopify_readiness_not_connected']);
    const unavailable = (value) => value?.blockers?.some((code) => unavailableBlockers.has(code));
    const fail = (error) => {
      state.blocked = true;
      state.failureReason = error.reason || 'price_mismatch';
      state.recoverable = state.failureReason === 'unavailable' && !env.failed?.();
      state.retryAt = error.retryAt || 0;
      return error;
    };
    const clearFailure = () => {
      state.blocked = false; state.recoverable = false; state.failureReason = null; state.retryAt = 0;
    };
    const endpoint = config.endpoint.replace(/\/$/, '');
    const storage = env.storage;
    const qa = typeof config.qaToken === 'string' && config.qaToken.length > 20;
    let generation = 0, initialized = false, fallbackPending, releaseNativeReady, pendingCartMutation = 0, ambiguousCartMutation = false, lockHeld = false;
    const withCartLock = (run) => env.withLock ? env.withLock(async () => {
      lockHeld = true;
      try { return await run(); } finally { lockHeld = false; }
    }) : run();
    const nativeReady = new Promise((resolve) => { releaseNativeReady = resolve; });
    const context = { themeId: config.themeId, currency: config.currency, country: config.country, preview: qa, ...(qa ? { qaToken: config.qaToken } : {}) };
    const validMarket = (market) => market?.country === config.country && market.currency === config.currency
      && Number.isFinite(Number(market.rate)) && Number(market.rate) > 0 && Number(market.rate) === Number(env.presentmentRate)
      && market.quantum === (config.currency === 'ISK' ? 100 : 1);
    const nativeConverted = (cents, market) => Math.round(cents * Number(market.rate) / market.quantum) * market.quantum;
    const nativeMarket = (requireAvailable = false) => {
      const catalog = config.catalog;
      const base = catalog.find((entry) => entry.role === 'base');
      const pack = catalog.find((entry) => entry.role === 'pack');
      if (catalog.length !== 13 || catalog.filter((entry) => entry.role === 'base').length !== 1
        || catalog.filter((entry) => entry.role === 'pack').length !== 12 || new Set(catalog.map((entry) => String(entry.variantId))).size !== 13
        || new Set(catalog.map((entry) => String(entry.productId))).size !== 13
        || catalog.some((entry) => !entry.productId || !entry.variantId || (entry.variantCount != null && entry.variantCount !== 1)
          || (requireAvailable && (entry.variantCount !== 1 || entry.available !== true))
          || !Number.isSafeInteger(entry.price) || entry.price <= 0 || (entry.role === 'pack' && entry.price !== pack.price)
        )) throw warn();
      const baseline = { baseCents: base.price, packCents: pack.price };
      const market = { country: config.country, currency: config.currency, rate: Number(env.presentmentRate),
        quantum: config.currency === 'ISK' ? 100 : 1, baseline };
      if (!validMarket(market) || (config.currency === 'EUR' && (market.rate !== 1 || base.price !== 3499 || pack.price !== 599))) throw warn();
      return market;
    };
    const signedAssignment = (proof, token) => {
      if (!validAssignment(proof, 'EUR') || proof.identityVerified !== true || proof.existing !== Boolean(token)
        || proof.nativeCartVerificationRequired !== true
        || proof.verification !== 'signed_assignment' || (token && proof.token !== token)
        || proof.requestedCurrency !== config.currency) throw warn('assignment_invalid');
      const rule = proof.pricingRule;
      const canonical = { baseCents: proof.arm === 'B' ? 3999 : 3499, packCents: proof.arm === 'C' ? 899 : 599 };
      if (rule?.currency !== 'EUR' || rule.baseline?.baseCents !== 3499 || rule.baseline?.packCents !== 599
        || rule.includedPacksPerBase !== 1 || rule.prices?.baseCents !== canonical.baseCents || rule.prices?.packCents !== canonical.packCents
        || proof.prices.baseCents !== canonical.baseCents || proof.prices.packCents !== canonical.packCents) throw warn();
      if (proof.catalog?.length !== 13 || config.catalog.some((entry) => !proof.catalog.some((verified) =>
        String(verified.productId) === String(entry.productId) && String(verified.variantId) === String(entry.variantId) && verified.role === entry.role))) throw warn();
      const market = nativeMarket(!token);
      // Shopify Liquid supplies native A prices. Only the changed B/C component
      // uses the same EUR conversion and rounding as the Shopify cart function.
      const prices = { baseCents: proof.arm === 'B' ? nativeConverted(canonical.baseCents, market) : market.baseline.baseCents,
        packCents: proof.arm === 'C' ? nativeConverted(canonical.packCents, market) : market.baseline.packCents };
      if (!Number.isSafeInteger(prices.baseCents) || prices.baseCents <= 0 || !Number.isSafeInteger(prices.packCents) || prices.packCents <= 0) throw warn();
      return { ...proof, currency: config.currency, prices, market };
    };
    const request = async (url, body, options = {}) => {
      const requestGeneration = generation;
      if (env.failed?.() && !options.nativeFallback) throw warn('unavailable');
      if (!options.nativeFallback) env.progress?.('request');
      const controller = new AbortController();
      const cartMutation = Boolean(body && !url.startsWith(endpoint) && !options.nativeFallback);
      let mutationResponse = false;
      if (cartMutation) pendingCartMutation += 1;
      let expired = false, timeout;
      const deadline = new Promise((_, reject) => {
        timeout = setTimeout(() => {
          expired = true;
          controller.abort();
          if (cartMutation) rememberUncertainMutation();
          reject(warn('unavailable', { ambiguousMutation: cartMutation }));
        }, env.requestTimeout || 8000);
      });
      const perform = async () => {
        const response = await env.fetch(url, {
          method: body ? 'POST' : 'GET', cache: 'no-store', credentials: url.startsWith(endpoint) ? 'omit' : 'same-origin',
          headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
          ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal,
          ...(options.keepalive ? { keepalive: true } : {}),
        });
        mutationResponse = true;
        if (expired || (!options.nativeFallback && env.failed?.()) || requestGeneration !== generation) throw warn('unavailable');
        if (!response.ok) {
          if (cartMutation && (!response.status || response.status >= 500)) rememberUncertainMutation();
          let failure;
          try { failure = await response.json(); } catch (error) { /* Status still identifies a transport failure. */ }
          const temporary = !response.status || [408, 425, 429].includes(response.status) || response.status >= 500;
          const retryAfter = response.headers?.get?.('Retry-After');
          const retryAt = retryAfter ? (/^\d+(\.\d+)?$/.test(retryAfter) ? now() + Number(retryAfter) * 1000 : Date.parse(retryAfter)) : 0;
          throw warn(temporary ? 'unavailable' : ['assignment_invalid', 'invalid_token', 'qa_token_invalid', 'qa_context_required'].includes(failure?.code)
            ? 'assignment_invalid' : failure?.code === 'assignment_conflict' ? 'identity_conflict' : 'price_mismatch',
          { retryAt: Number.isFinite(retryAt) ? retryAt : 0 });
        }
        const result = await response.json();
        if (expired || (!options.nativeFallback && env.failed?.()) || requestGeneration !== generation) throw warn('unavailable');
        return result;
      };
      try { return await Promise.race([perform(), deadline]); } catch (error) {
        if (cartMutation && !mutationResponse) rememberUncertainMutation();
        throw error.reason ? error : warn('unavailable', { ambiguousMutation: cartMutation && !mutationResponse });
      } finally { if (cartMutation) pendingCartMutation -= 1; clearTimeout(timeout); }
    };
    const fetchCart = () => request(`${config.cartUrl}.js`, undefined, { nativeFallback: state.nativeExcluded || state.nativeExclusionRequested });
    const rememberUncertainMutation = () => {
      ambiguousCartMutation = true;
      try { storage.write({ ...storage.read(), nativeMutationUncertain: true }); }
      catch (error) { /* This page still refuses a second write without proof. */ }
    };
    const cartExcluded = (cart) => cart?.attributes?.[EXCLUDED_ATTRIBUTE] === EXCLUDED_VALUE;
    const verifyNativeCart = (cart) => {
      const baseline = nativeMarket().baseline;
      if (cart.currency !== config.currency || !cartExcluded(cart)
        || OWNED_ATTRIBUTES.some((key) => cart.attributes?.[key])) throw warn();
      let totals;
      try { totals = expectedTotal(cart.items || [], config.catalog, baseline, config.currency); }
      catch (error) { throw warn(); }
      if (discountCodes(cart).length) {
        // Shopify can legitimately replace the bundle discount with a coupon.
        // Still require native original prices and never accept surcharges.
        for (const item of cart.items || []) {
          const product = config.catalog.find((entry) => String(entry.productId) === String(item.product_id));
          if (!product) continue;
          const unit = product.role === 'base' ? baseline.baseCents : baseline.packCents;
          if (item.original_line_price !== item.quantity * unit || item.final_line_price < 0
            || item.final_line_price > item.original_line_price) throw warn();
        }
      } else if (!totals.valid || totals.expected !== totals.actual) throw warn();
      return cart;
    };
    const itemQuantities = (cart) => {
      const quantities = new Map();
      for (const item of cart?.items || []) {
        const id = String(item.variant_id || item.id);
        quantities.set(id, (quantities.get(id) || 0) + item.quantity);
      }
      return JSON.stringify([...quantities].sort(([a], [b]) => a.localeCompare(b)));
    };
    const persistNativeExclusion = () => {
      try { storage.write({ nativeExcluded: true, ...(storage.read()?.nativeMutationUncertain ? { nativeMutationUncertain: true } : {}) }); }
      catch (error) { /* The native cart marker remains authoritative without storage. */ }
    };
    const fallbackToNative = (cart) => {
      if (fallbackPending) return fallbackPending;
      if (qa) return Promise.reject(warn('unavailable'));
      if (state.failureReason && state.failureReason !== 'unavailable') return Promise.reject(warn(state.failureReason));
      // An interrupted Shopify write may still commit server-side. Do not race
      // a second write against it or claim that native prices are now stable.
      if (pendingCartMutation || ambiguousCartMutation || storage.read()?.nativeMutationUncertain) return Promise.reject(fail(warn('unavailable', { ambiguousMutation: true })));
      generation += 1;
      state.nativeExclusionRequested = true;
      state.blocked = true;
      const reloadRequired = initialized || Boolean(config.cartHasAssignment || OWNED_ATTRIBUTES.some((key) => cart?.attributes?.[key]));
      persistNativeExclusion();
      const migrate = async () => {
        if (storage.read()?.nativeMutationUncertain) throw warn('unavailable', { ambiguousMutation: true });
        nativeMarket();
        let nativeCart = cart || await request(`${config.cartUrl}.js`, undefined, { nativeFallback: true });
        const originalCart = nativeCart;
        state.reloadRequired ||= OWNED_ATTRIBUTES.some((key) => originalCart.attributes?.[key]);
        if (!cartExcluded(nativeCart) || OWNED_ATTRIBUTES.some((key) => nativeCart.attributes?.[key])) {
          await request(`${config.cartUrl}/update.js`, {
            attributes: { ...Object.fromEntries(OWNED_ATTRIBUTES.map((key) => [key, ''])), [EXCLUDED_ATTRIBUTE]: EXCLUDED_VALUE },
          }, { nativeFallback: true });
          nativeCart = await request(`${config.cartUrl}.js`, undefined, { nativeFallback: true });
        }
        if (itemQuantities(originalCart) !== itemQuantities(nativeCart)) throw warn();
        verifyNativeCart(nativeCart);
        state.active = false;
        state.assignment = null;
        state.baseline = nativeMarket().baseline;
        state.nativeExcluded = true;
        state.excludedByUnavailable = true;
        state.reloadRequired ||= reloadRequired;
        clearFailure();
        persistNativeExclusion();
        releaseNativeReady(state);
        return nativeCart;
      };
      const lockedMigration = async () => {
        if (lockHeld || !env.withLock) return migrate();
        let expired = false, timeout;
        const deadline = new Promise((_, reject) => {
          timeout = setTimeout(() => { expired = true; reject(warn('unavailable')); }, env.fallbackLockTimeout || 9000);
        });
        try { return await Promise.race([withCartLock(() => {
          if (expired) throw warn('unavailable');
          clearTimeout(timeout);
          return migrate();
        }), deadline]); }
        finally { clearTimeout(timeout); }
      };
      fallbackPending = lockedMigration().catch((error) => { throw fail(error); }).finally(() => { fallbackPending = null; });
      return fallbackPending;
    };
    const reloadNativeCart = () => {
      env.reload?.();
      throw warn('native_reload', { reloadRequired: true });
    };
    const properties = () => state.active ? Object.fromEntries(Object.entries(ATTRS)
      .map(([field, key]) => [key, state.assignment[field]])) : {};
    const cartAttributes = () => ({ ...properties(), _flipzy_price_cohort: `${qa ? 'flipzy-abc-qa-20261025' : EXPERIMENT}:${state.assignment.arm}`, _flipzy_currency: state.assignment.currency,
      _flipzy_base_cents: String(state.assignment.prices.baseCents), _flipzy_pack_cents: String(state.assignment.prices.packCents) });
    const verifyCart = (cart) => {
      if (state.nativeExcluded) return verifyNativeCart(cart);
      if (!state.active) {
        if (!state.excludedByDiscount) return cart;
        const base = config.catalog.find((entry) => entry.role === 'base')?.price;
        const pack = config.catalog.find((entry) => entry.role === 'pack')?.price;
        if (!base || !pack || cart.currency !== config.currency) return cart;
        state.discountCodes = discountCodes(cart);
        const totals = expectedTotal(cart.items || [], config.catalog, { baseCents: base, packCents: pack }, config.currency);
        state.discountConflict = state.discountCodes.length > 0 && totals.units > 0 && (!totals.valid || totals.expected !== totals.actual);
        state.blocked = state.discountConflict;
        state.recoverableDiscount = state.discountConflict;
        if (state.discountConflict) throw fail(warn('discount_conflict'));
        clearFailure();
        return cart;
      }
      if (cart.currency !== state.assignment.currency) throw fail(warn());
      if (cart.attributes?.[ATTRS.token] !== state.assignment.token) throw fail(warn('identity_conflict'));
      let totals;
      try { totals = expectedTotal(cart.items || [], config.catalog, state.assignment.prices, state.assignment.currency); }
      catch (error) { throw fail(warn()); }
      if (!totals.valid || totals.expected !== totals.actual) {
        state.blocked = true;
        state.discountCodes = discountCodes(cart);
        state.recoverableDiscount = state.discountCodes.length > 0;
        throw fail(warn(state.recoverableDiscount ? 'discount_conflict' : 'price_mismatch'));
      }
      if (!state.blocked || state.recoverable || state.recoverableDiscount) {
        state.recoverableDiscount = false; state.discountCodes = []; clearFailure();
      }
      return cart;
    };
    const syncCart = async (cart) => {
      if (cartExcluded(cart) || storage.read()?.nativeExcluded || state.nativeExclusionRequested) {
        const nativeCart = await fallbackToNative(cart);
        if (state.reloadRequired) reloadNativeCart();
        return nativeCart;
      }
      const existingToken = cart.attributes?.[ATTRS.token];
      if (existingToken && existingToken !== state.assignment.token && cart.items?.length) throw warn('identity_conflict');
      const attributes = cartAttributes();
      if (Object.entries(attributes).some(([key, value]) => cart.attributes?.[key] !== value)) {
        cart = await request(`${config.cartUrl}/update.js`, { attributes });
      }
      return verifyCart(cart);
    };
    const initialize = async () => {
      const initializationGeneration = generation;
      // A watchdog migration may already be queued behind this initial lock.
      // Release the lock for that migration instead of awaiting it recursively.
      if (state.nativeExclusionRequested && fallbackPending) return state;
      if (env.failed?.()) { fail(warn('unavailable')); return state; }
      // Public enrollment requires the published target. Private QA always requires a server-signed token.
      if (!config.enabled || String(env.theme?.id) !== config.themeId || (env.theme?.role !== 'main' && !qa) || env.designMode) return state;
      let remembered;
      let cart;
      try {
        remembered = storage.read();
        // A storage write can fail after a valid response. Keep that already
        // received identity in memory so recovery cannot enroll another arm.
        if (!remembered?.token && state.assignment?.token) remembered = { token: state.assignment.token };
        // Existing identity verification is independent of the cart read.
        // Fresh enrollment starts only after the actual cart was inspected.
        const excluded = remembered?.nativeExcluded || config.cartNativeExcluded || state.nativeExclusionRequested;
        const restoring = !qa && !excluded && Boolean(remembered?.token);
        const cartTask = fetchCart();
        const statusTask = (restoring ? request(`${endpoint}/restore`, { token: remembered.token, ...context })
          : qa && !excluded ? request(`${endpoint}/status`, context) : Promise.resolve(null))
          .then((value) => ({ status: 'fulfilled', value }), (reason) => ({ status: 'rejected', reason }));
        const [cartResult] = await Promise.allSettled([cartTask]);
        if (cartResult.status === 'rejected') throw cartResult.reason;
        cart = cartResult.value;
        if (excluded || cartExcluded(cart)) {
          if (fallbackPending) return state;
          await fallbackToNative(cart);
          return state;
        }
        if (env.failed?.() || initializationGeneration !== generation) throw warn('unavailable');
        const statusResult = await statusTask;
        if (env.failed?.() || initializationGeneration !== generation) throw warn('unavailable');
        if (qa && statusResult.status === 'rejected') throw statusResult.reason;
        if (qa && unavailable(statusResult.value)) throw warn('unavailable');
        let cartToken = cart.attributes?.[ATTRS.token];
        if (qa && config.qaTokenChanged && (remembered?.token || cartToken)) {
          if (cart.items?.length) throw warn('identity_conflict');
          remembered = null;
          cartToken = undefined;
        }
        if (remembered?.token && cartToken && remembered.token !== cartToken && cart.items?.length) throw warn('identity_conflict');
        const token = remembered?.token || cartToken;
        // Once native A was shown without enrollment, a transport retry must
        // not turn the same page into a newly assigned B/C offer.
        if ((state.excludedByUnavailable || state.excludedByDiscount) && !state.assignment) {
          if (token) throw warn('identity_conflict');
          clearFailure();
          verifyCart(cart);
          return state;
        }
        if (!token && discountCodes(cart).length) {
          state.discountCodes = discountCodes(cart);
          state.excludedByDiscount = true;
          verifyCart(cart);
          return state;
        }
        let assignment;
        let baseline;
        if (token && !qa) {
          if (restoring && statusResult.status === 'rejected') throw statusResult.reason;
          const proof = restoring ? statusResult.value : await request(`${endpoint}/restore`, { token, ...context });
          assignment = signedAssignment(proof, token);
          baseline = assignment.market.baseline;
        } else if (!qa) {
          nativeMarket(true);
          if (!storage.available() || env.canEnroll === false) { clearFailure(); return state; }
          state.enrollmentAttempted = true;
          const proof = await request(`${endpoint}/enroll`, { ...context,
            catalog: config.catalog.map(({ productId, variantId, role, variantCount, available }) => ({ productId, variantId, role, variantCount, available })) });
          if (unavailable(proof)) throw warn('unavailable');
          if (proof.eligible === false && !proof.token && !proof.preservedAssignment) { clearFailure(); return state; }
          assignment = signedAssignment(proof);
          baseline = assignment.market.baseline;
        } else {
          if (statusResult.status === 'rejected') throw statusResult.reason;
          const status = statusResult.value;
          if (unavailable(status)) throw warn('unavailable');
          if (!status.eligible && !token) { clearFailure(); return state; }
          baseline = status.arms?.find((entry) => entry.arm === 'A')?.prices;
          if (status.currency !== config.currency || !validMarket(status.market) || !baseline || config.catalog.length !== 13 || config.catalog.some((entry) =>
            !entry.productId || !entry.variantId || (entry.variantCount != null && entry.variantCount !== 1)
            || entry.price !== (entry.role === 'base' ? baseline.baseCents : baseline.packCents))) {
            if (token) throw warn();
            clearFailure();
            return state;
          }
          const nativeB = status.arms?.find((entry) => entry.arm === 'B')?.prices;
          const nativeC = status.arms?.find((entry) => entry.arm === 'C')?.prices;
          if (nativeB?.baseCents !== nativeConverted(3999, status.market) || nativeB.packCents !== baseline.packCents
            || nativeC?.baseCents !== baseline.baseCents || nativeC.packCents !== nativeConverted(899, status.market)) {
            if (token) throw warn();
            clearFailure();
            return state;
          }
          if (!token && (!storage.available() || env.canEnroll === false)) { clearFailure(); return state; }
          state.enrollmentAttempted = true;
          assignment = await request(`${endpoint}/assignment`, { ...(token ? { token } : {}),
            ...context });
          if (unavailable(assignment)) throw warn('unavailable');
          const expectedPrices = status.arms?.find((entry) => entry.arm === assignment.arm)?.prices;
          if (!validAssignment(assignment, config.currency, qa) || !validMarket(assignment.market) || !expectedPrices
            || assignment.prices.baseCents !== expectedPrices.baseCents || assignment.prices.packCents !== expectedPrices.packCents) {
            if (token || assignment.preservedAssignment) throw warn();
            clearFailure();
            return state;
          }
        }
        state.baseline = baseline;
        state.assignment = assignment;
        state.active = true;
        // Persist identity before the cart roundtrip. A retry must restore, never rerandomize.
        try { storage.write({ token: assignment.token, ...(storage.read()?.nativeMutationUncertain ? { nativeMutationUncertain: true } : {}) }); }
        catch (error) { throw warn('unavailable'); }
        await syncCart(cart);
      } catch (error) {
        if (initializationGeneration === generation) {
          if (error.reloadRequired) return state;
          if (error.reason === 'unavailable' && !qa) {
            try { await fallbackToNative(cart); }
            catch (nativeError) { fail(nativeError); }
          } else if (remembered?.token || cart?.attributes?.[ATTRS.token] || state.assignment || state.enrollmentAttempted || error.reason === 'unavailable') fail(error);
        }
      }
      return state;
    };
    const unitPrice = (role, original) => {
      if (!state.active) return Number(original);
      const key = role === 'base' ? 'baseCents' : 'packCents';
      if (Number(original) !== state.baseline[key]) throw fail(warn());
      return state.assignment.prices[key];
    };
    let retryPending;
    const retry = () => {
      if (retryPending) return retryPending;
      retryPending = (async () => {
        await state.ready;
        if (!state.recoverable || env.failed?.() || state.retryAt > now()) return state;
        const retryGeneration = ++generation;
        let timer;
        const expired = new Promise((resolve) => {
          timer = setTimeout(() => {
            generation += 1;
            fail(warn('unavailable'));
            resolve(state);
          }, env.retryTimeout || 33000);
        });
        const run = () => retryGeneration === generation ? initialize() : state;
        try {
          return await Promise.race([withCartLock(run), expired]);
        } catch (error) { fail(error.reason ? error : warn('unavailable')); return state; }
        finally { clearTimeout(timer); }
      })().finally(() => { retryPending = null; });
      return retryPending;
    };
    const prepareCart = async () => {
      await state.ready;
      if (state.recoverable) await retry();
      return withCartLock(prepareLockedCart);
    };
    const prepareLockedCart = async () => {
      if (state.reloadRequired) reloadNativeCart();
      if (state.blocked && !state.recoverableDiscount) throw warn(state.failureReason || 'price_mismatch');
      try {
        if (state.nativeExcluded || state.nativeExclusionRequested || storage.read()?.nativeExcluded) {
          let cart = await fetchCart();
          if (!cartExcluded(cart) || OWNED_ATTRIBUTES.some((key) => cart.attributes?.[key])) cart = await fallbackToNative(cart);
          else verifyNativeCart(cart);
          if (state.reloadRequired) reloadNativeCart();
          return cart;
        }
        if (!state.active) {
          if (!state.excludedByDiscount && !state.excludedByUnavailable) return null;
          const cart = await fetchCart();
          if (storage.read()?.token || cart.attributes?.[ATTRS.token]) throw warn('identity_conflict');
          return verifyCart(cart);
        }
        const remembered = storage.read();
        if (remembered?.token && remembered.token !== state.assignment.token) throw warn('identity_conflict');
        return await syncCart(await fetchCart());
      }
      catch (error) {
        if (error.reloadRequired) throw error;
        if (error.reason === 'unavailable' && !qa) {
          await fallbackToNative();
          reloadNativeCart();
        }
        throw fail(error);
      }
    };
    const removeDiscount = async (code) => {
      await state.ready;
      if (!state.discountCodes.includes(code)) throw warn();
      const cart = await fetchCart();
      const remaining = discountCodes(cart).filter((entry) => entry.toLowerCase() !== code.toLowerCase());
      // Only the shopper's explicit button click removes this named code.
      return request(`${config.cartUrl}/update.js`, { discount: remaining.join(',') });
    };
    const event = async (name, analyticsConsent) => {
      if (!state.active || state.blocked || qa || analyticsConsent !== true) return false;
      try {
        await request(`${endpoint}/${name === 'exposure' ? 'exposure' : 'event'}`, {
          token: state.assignment.token, analyticsConsent: true, ...context, ...(name === 'exposure' ? {} : { event: name }),
        }, { keepalive: true });
        return true;
      } catch (error) { return false; /* Tracking failure must not change the shopper's price or arm. */ }
    };
    const client = { state, properties, unitPrice, prepareCart, verifyCart, removeDiscount, event, retry, fallbackToNative };
    // Lock the first read, assignment and persistence together across tabs.
    state.ready = Promise.race([withCartLock(initialize), nativeReady])
      .then((value) => { initialized = true; return value; });
    client.ready = state.ready;
    return client;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { createClient, validAssignment, expectedTotal };
    return;
  }
  if (root.FlipzyPriceTestBoot?.failed) return;
  if (root.FlipzyPriceTestBoot) {
    root.FlipzyPriceTestBoot.started = true;
    root.FlipzyPriceTestBoot.waitFor?.('lock');
  }
  const configElement = root.document?.getElementById('FlipzyPriceTestConfig');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  let qaStorage;
  try {
    qaStorage = root.sessionStorage;
    const saved = JSON.parse(qaStorage.getItem(`${STORAGE}:qa`) || 'null');
    const explicitToken = new URL(root.location.href).searchParams.get('flipzy_price_preview');
    config.qaToken = explicitToken || saved?.qaToken;
    config.qaTokenChanged = Boolean(explicitToken && explicitToken !== saved?.qaToken);
  } catch (error) { /* Unavailable session storage cannot authorize private QA. */ }
  const storageKey = config.qaToken ? `${STORAGE}:qa` : STORAGE;
  const storage = {
    read() { try { return JSON.parse((config.qaToken ? qaStorage : root.localStorage).getItem(storageKey) || 'null'); } catch (error) { return null; } },
    write(value) { (config.qaToken ? qaStorage : root.localStorage).setItem(storageKey, JSON.stringify({ ...value, ...(config.qaToken ? { qaToken: config.qaToken } : {}) })); },
    available() {
      try {
        const target = config.qaToken ? qaStorage : root.localStorage;
        const value = target.getItem(storageKey);
        target.setItem(storageKey, value === null ? '{}' : value);
        if (value === null) target.removeItem(storageKey);
        return true;
      } catch (error) { return false; }
    },
  };
  const locks = root.navigator?.locks;
  const client = createClient(config, { fetch: root.fetch.bind(root), storage, theme: root.Shopify?.theme, presentmentRate: root.Shopify?.currency?.rate, designMode: root.Shopify?.designMode,
    canEnroll: Boolean(locks), failed: () => root.FlipzyPriceTestBoot?.failed,
    reload: () => root.location.reload(),
    requestTimeout: root.FlipzyPriceTestBoot?.requestTimeout, progress: (stage) => root.FlipzyPriceTestBoot?.waitFor?.(stage),
    ...(locks ? { withLock: (run) => locks.request(storageKey, run) } : {}) });
  root.FlipzyPriceTest = client;
  const doc = root.document;
  const money = (cents) => new Intl.NumberFormat(config.locale || doc.documentElement.lang, { style: 'currency', currency: config.currency }).format(cents / 100);
  const consent = () => root.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  let viewed = false;
  let exposed = false;
  let exposurePending = false;
  let exposureAttempts = 0;
  const expose = () => {
    if (!viewed || exposed || exposurePending || !consent() || !client.state.active || client.state.blocked) return;
    exposurePending = true;
    exposureAttempts += 1;
    client.event('exposure', true).then((recorded) => {
      exposurePending = false;
      exposed = recorded;
      if (!recorded && exposureAttempts < 3) root.setTimeout(expose, exposureAttempts * 2000);
    });
  };
  const decorate = (scope = doc) => {
    if (!client.state.active || (client.state.blocked && !client.state.recoverableDiscount)) return;
    scope.querySelectorAll('[data-flipzy-test-price]').forEach((element) => {
      const value = money(client.unitPrice(element.dataset.flipzyTestPrice, Number(element.dataset.flipzyCatalogPrice)));
      if (element.textContent.trim() !== value) element.textContent = value;
    });
    scope.querySelectorAll('[data-flipzy-test-faq-price]').forEach((element) => {
      if (element.dataset.flipzyTestSchemaReady) return;
      const schema = JSON.parse(element.textContent);
      const original = element.dataset.flipzyTestFaqPrice;
      for (const question of schema.mainEntity || []) if (question.acceptedAnswer?.text) {
        question.acceptedAnswer.text = question.acceptedAnswer.text.split(original).join(money(client.state.assignment.prices.packCents));
      }
      element.dataset.flipzyTestSchemaReady = 'true';
      element.textContent = JSON.stringify(schema);
    });
    scope.querySelectorAll('[data-flipzy-test-product-schema]').forEach((element) => {
      if (element.dataset.flipzyTestSchemaReady) return;
      const schema = JSON.parse(element.textContent);
      const price = client.state.assignment.prices[element.dataset.flipzyTestProductSchema === 'base' ? 'baseCents' : 'packCents'];
      for (const offer of Array.isArray(schema.offers) ? schema.offers : [schema.offers]) if (offer?.priceCurrency === config.currency) offer.price = (price / 100).toFixed(2);
      element.dataset.flipzyTestSchemaReady = 'true';
      element.textContent = JSON.stringify(schema);
    });
    scope.querySelectorAll('product-form form').forEach((form) => {
      for (const [key, value] of Object.entries(client.properties())) {
        let input = form.querySelector(`input[name="properties[${key}]"]`);
        if (!input) { input = doc.createElement('input'); input.type = 'hidden'; input.name = `properties[${key}]`; form.append(input); }
        input.value = value;
      }
    });
  };
  const b2bPage = root.FlipzyPriceTestBoot?.b2bPage === true || Boolean(doc.querySelector('main .flipzy-b2b'));
  const warn = (force = false) => {
    const destination = doc.querySelector('cart-drawer.active .drawer__footer') || doc.querySelector('main product-form')
      || doc.querySelector('main .flipzy-routine-offer__header') || (!b2bPage || force ? doc.querySelector('main') : null);
    // An informational visit is not a checkout attempt. Keep the blocked state
    // and commerce guards, but surface its warning where commerce is used.
    if (!destination) return;
    let message = doc.querySelector('[data-flipzy-price-test-error]');
    if (!message) {
      message = doc.createElement('p');
      message.dataset.flipzyPriceTestError = '';
      message.setAttribute('role', 'alert');
    }
    if (message.parentElement !== destination) destination.prepend(message);
    message.dataset.failureReason = client.state.failureReason || 'price_mismatch';
    message.textContent = client.state.failureReason === 'unavailable' ? config.unavailableWarning : config.warning;
    if (client.state.recoverable) {
      const button = doc.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary';
      button.dataset.flipzyPriceRetry = '';
      button.textContent = config.retryLabel;
      message.append(' ', button);
    }
    for (const code of client.state.discountCodes) {
      const button = doc.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary';
      button.textContent = config.removeDiscountLabel.replace('[code]', code);
      button.addEventListener('click', async () => {
        button.disabled = true;
        try { await client.removeDiscount(code); root.location.assign(config.cartUrl); }
        catch (error) { warn(); }
      });
      message.append(' ', button);
    }
  };
  doc.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-flipzy-price-retry]');
    if (!button) return;
    event.preventDefault();
    if (root.FlipzyPriceTestBoot?.failed) { root.location.reload(); return; }
    button.disabled = true;
    try {
      await client.retry();
      if (client.state.reloadRequired) { root.location.reload(); return; }
      if (client.state.blocked) { warn(true); return; }
      decorate();
      doc.querySelector('[data-flipzy-price-test-error]')?.remove();
      doc.dispatchEvent(new CustomEvent('flipzy:price-test-recovered'));
      observeClient();
    } catch (error) { if (!error.reloadRequired) warn(true); }
    finally { if (button.isConnected) button.disabled = false; }
  });
  // Gate native forms and checkout as well as the custom builder integrations.
  const nextTask = () => new Promise((resolve) => root.setTimeout(resolve, 0));
  const flushCheckoutEvent = async () => {
    let deadline;
    try {
      await Promise.race([
        client.event('checkout_started', consent()),
        new Promise((resolve) => { deadline = root.setTimeout(resolve, 1000); }),
      ]);
    } catch (error) { /* Analytics delivery cannot prevent native checkout. */ }
    finally { clearTimeout(deadline); }
  };
  const allowedSubmits = new WeakSet();
  doc.addEventListener('submit', async (event) => {
    const form = event.target;
    const submitter = event.submitter;
    const checkout = event.submitter?.name === 'checkout' || /\/checkout(?:\?|$)/.test(form.action || '');
    const variant = form.querySelector('input[name="id"]')?.value;
    if (!checkout && (!form.closest('product-form') || !config.catalog.some((item) => String(item.variantId) === variant))) return;
    if (allowedSubmits.has(form)) { allowedSubmits.delete(form); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    try {
      await client.prepareCart(); decorate();
      // A resolved promise can resume before native submit dispatch finishes.
      // Wait one task so requestSubmit is not silently ignored by the browser.
      await nextTask();
      if (checkout) await flushCheckoutEvent();
      if (client.state.blocked || root.FlipzyPriceTestBoot?.blocked) throw new Error(config.warning);
      if (!form.isConnected || (submitter && submitter.form !== form)) throw new Error(config.warning);
      allowedSubmits.add(form);
      try { form.requestSubmit(submitter || undefined); }
      finally { allowedSubmits.delete(form); }
    } catch (error) { if (!error.reloadRequired) warn(true); }
  }, true);
  doc.addEventListener('visitorConsentCollected', expose);
  root.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    try {
      const replacement = JSON.parse(event.newValue || 'null');
      if (replacement?.nativeExcluded || client.state.nativeExcluded || client.state.nativeExclusionRequested) {
        client.fallbackToNative().then(() => root.location.reload()).catch(() => warn(true));
        return;
      }
      if (!client.state.active || replacement?.token === client.state.assignment.token) return;
    } catch (error) { /* An unreadable replacement is not a new valid assignment. */ }
    if (!client.state.active) return;
    client.state.blocked = true;
    client.state.recoverable = false;
    client.state.failureReason = 'identity_conflict';
    warn();
  });
  // Locale-aware ordinary checkout links also need the same last cart check.
  const allowedLinks = new WeakSet();
  doc.addEventListener('click', async (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || !/\/checkout(?:\?|$)/.test(link.pathname + link.search) || allowedLinks.has(link)) {
      if (link) allowedLinks.delete(link);
      return;
    }
    event.preventDefault();
    try {
      await client.prepareCart(); await nextTask();
      await flushCheckoutEvent();
      if (client.state.blocked || root.FlipzyPriceTestBoot?.blocked) throw new Error(config.warning);
      allowedLinks.add(link);
      try { link.click(); } finally { allowedLinks.delete(link); }
    }
    catch (error) { if (!error.reloadRequired) warn(true); }
  }, true);
  let observingClient = false;
  const observeClient = () => {
    if (observingClient || (!client.state.active && !client.state.excludedByDiscount) || client.state.blocked) return;
    observingClient = true;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source }) => {
        if (['product-form', 'flipzy-base-addon', 'flipzy-routine-offer'].includes(source)) client.event('cart_added', consent());
      });
    }
    if (doc.querySelector('[data-flipzy-test-product-schema]')) client.event('product_viewed', consent());
    // Shopify's privacy API is loaded only for an enrolled visitor, never while off.
    if (client.state.active && !client.state.assignment.qa && !root.Shopify?.customerPrivacy && root.Shopify?.loadFeatures) root.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], expose);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) for (const node of mutation.addedNodes) if (node.nodeType === 1) {
        try {
          decorate(node.parentElement || doc);
          if ((client.state.recoverableDiscount || client.state.discountConflict) && !doc.querySelector('[data-flipzy-price-test-error]')) warn();
        } catch (error) { client.state.blocked = true; client.state.recoverable = false; client.state.failureReason = 'price_mismatch'; warn(); }
      }
    });
    observer.observe(doc.body, { childList: true, subtree: true });
    if (!client.state.active || client.state.assignment.qa) return;
    const prices = doc.querySelectorAll('[data-flipzy-test-price], [data-board-option]');
    const visibility = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { viewed = true; expose(); visibility.disconnect(); }
    });
    prices.forEach((element) => visibility.observe(element));
  };
  client.ready.then(() => {
    if (client.state.reloadRequired) { root.location.reload(); return; }
    root.FlipzyPriceTestBoot?.finish();
    try { decorate(); } catch (error) { client.state.blocked = true; client.state.recoverable = false; client.state.failureReason = 'price_mismatch'; }
    doc.documentElement.classList.remove('flipzy-price-test-pending');
    doc.dispatchEvent(new CustomEvent('flipzy:price-test-ready'));
    if (client.state.blocked || client.state.discountConflict) warn();
    // A failed remembered assignment may leave the client inactive. Observe
    // the drawer before that early return so opening it still explains why
    // checkout is blocked, without putting an alert above the B2B content.
    const drawer = b2bPage ? doc.querySelector('cart-drawer') : null;
    if (drawer) {
      const cartWarningObserver = new MutationObserver(() => {
        if (!client.state.blocked && !client.state.discountConflict) return;
        const footer = doc.querySelector('cart-drawer.active .drawer__footer');
        if (footer && !footer.querySelector('[data-flipzy-price-test-error]')) warn();
      });
      cartWarningObserver.observe(drawer, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
    }
    observeClient();
  });
})(typeof window === 'undefined' ? globalThis : window);
